import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useHandlingList from '@afosto/queries/fulfilment/useHandlingList';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { FormattedMessage } from 'react-intl';
import { Link, useHistory, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Pick from './Pick';
import Putaway from './Putaway';
import ProcessHandlingListContext from '../../components/ProcessHandlingListContext';
import Page from '@afosto/app-components-react/Page';
import translations from './translations';

const ProcessHandlingList = () => {
  usePrivate();

  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:handling:read',
    'lcs:handling:write',
    'lcs:locations:read',
    'lcs:locations:write',
    'lcs:shipments:read',
    'lcs:shipments:write',
  ]);

  const history = useHistory();
  const intl = useIntl();
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { handlingListID, secondAction } = useParams();
  const { data: handlingList, isLoading: isFetchingHandlingList } = useHandlingList(handlingListID);
  const [wmsLocation] = useState(null);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPickAllDialog, setShowPickAllDialog] = useState(false);
  const shouldPack = secondAction === 'pack' && handlingList?.shipments?.length === 1;

  if (!hasPermissions()) {
    return (
      <Page title={handlingList?.number} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (handlingList?.is_finished) {
    return (
      <Page title={handlingList?.number} fullWidth>
        <Alert
          severity="success"
          action={
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => history.replace(`/handling-lists/${handlingList?.id}`)}
            >
              {intl.formatMessage(translations.finishedAlert.actions.viewList)}
            </Button>
          }
        >
          <FormattedMessage {...translations.finishedAlert.message} />
        </Alert>
      </Page>
    );
  }

  const secondaryActions = [
    ...(handlingList?.is_outbound
      ? [
          {
            key: 'picking',
            text: intl.formatMessage(
              translations.secondaryActions[shouldPack ? 'pickAndPackAll' : 'pickAll'],
            ),
            action: () => setShowPickAllDialog(true),
          },
        ]
      : []),
    ...(!handlingList?.is_outbound
      ? [
          {
            key: 'addItem',
            text: intl.formatMessage(translations.secondaryActions.addItem),
            action: () => setShowAddItemDialog(true),
            shortCut: true,
            icon: ['fal', 'plus'],
          },
          {
            key: 'history',
            text: intl.formatMessage(translations.secondaryActions.history),
            action: () => setShowHistory(true),
            shortCut: true,
            icon: ['fal', 'history'],
          },
        ]
      : []),
  ];

  return (
    <ProcessHandlingListContext.Provider
      value={{
        handlingList,
        isFetchingHandlingList,
        showAddItemDialog,
        setShowAddItemDialog,
        showHistory,
        setShowHistory,
        showPickAllDialog,
        setShowPickAllDialog,
        shouldPack,
        wmsLocation,
      }}
    >
      <Page
        isLoading={isFetchingHandlingList}
        disablePadding={!isDesktop}
        linkComponent={Link}
        title={handlingList?.number}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumbs.handlingLists),
            path: '/handling-lists',
          },
          {
            label: handlingList?.number,
            path: `/handling-lists/${handlingList?.id}/process`,
            active: true,
          },
        ]}
        useBreadcrumbNavigation={false}
        secondaryActions={secondaryActions}
        fullWidth
      >
        {handlingList && handlingList?.is_outbound && <Pick />}
        {handlingList && !handlingList?.is_outbound && <Putaway />}
      </Page>
    </ProcessHandlingListContext.Provider>
  );
};

export default ProcessHandlingList;
